/**
 * Scoped keyboard-shortcut registry.
 *
 * * Combos: `"v"`, `"shift+r"`, `"ctrl+z"`, `"mod+shift+z"` (`mod` = ⌘ on macOS, Ctrl
 *   elsewhere), `"alt+l"`, `"["`, `"]"`, `"1"`…`"9"`, `"enter"`, `"escape"`/`"esc"`,
 *   `"backspace"`, `"delete"`/`"del"`, `"space"`, `"arrowleft"`/`"left"`, `"?"`, `"plus"`.
 *   Modifiers must match exactly; for punctuation keys (`[`, `?`, …) Shift is ignored
 *   unless the combo names it, because layouts need Shift to type them.
 * * Scopes: every binding belongs to a scope (default `"global"`). Only bindings of the
 *   scope on top of the scope stack fire. Modals and open menus push their own scope, so
 *   page shortcuts pause while they are open (`useHotkeyScope`).
 * * Typing targets (text inputs, textareas, selects, contenteditable) are ignored unless a
 *   binding sets `allowInInputs`.
 * * The most recently registered matching binding wins; a handler returning `false`
 *   passes the event on to the next match. Handled events are `preventDefault()`ed.
 */
import { useEffect, useLayoutEffect, useRef } from 'react';

export type HotkeyHandler = (event: KeyboardEvent) => void | false;

export interface HotkeyOptions {
  /** Scope of the binding (default `"global"`). */
  scope?: string;
  /** Fire while focus is in a text field (default `false`). */
  allowInInputs?: boolean;
  /** `preventDefault()` handled events (default `true`). */
  preventDefault?: boolean;
  /** Fire on auto-repeated keydowns while the key is held (default `false`). */
  repeat?: boolean;
  /** Shown in shortcut help lists ({@link listHotkeys}). */
  description?: string;
}

export interface Combo {
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
}

export const GLOBAL_SCOPE = 'global';

let macCache: boolean | null = null;

/** True on macOS / iOS (decides what `mod` means and how combos are displayed). */
export function isMacPlatform(): boolean {
  if (macCache === null) {
    macCache = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }
  return macCache;
}

const KEY_ALIASES: Readonly<Record<string, string>> = {
  esc: 'escape',
  del: 'delete',
  return: 'enter',
  space: ' ',
  spacebar: ' ',
  plus: '+',
  left: 'arrowleft',
  right: 'arrowright',
  up: 'arrowup',
  down: 'arrowdown',
};

/** Parses a combo string (see module docs). Throws on an empty or malformed combo. */
export function parseCombo(combo: string): Combo {
  const parts = combo
    .toLowerCase()
    .split('+')
    .map((p) => p.trim());
  const rawKey = parts.pop();
  if (!rawKey || parts.some((p) => p === '')) throw new SyntaxError(`invalid hotkey "${combo}"`);
  const result: Combo = {
    key: KEY_ALIASES[rawKey] ?? rawKey,
    ctrl: false,
    alt: false,
    shift: false,
    meta: false,
  };
  for (const mod of parts) {
    switch (mod) {
      case 'ctrl':
      case 'control':
        result.ctrl = true;
        break;
      case 'alt':
      case 'option':
        result.alt = true;
        break;
      case 'shift':
        result.shift = true;
        break;
      case 'meta':
      case 'cmd':
        result.meta = true;
        break;
      case 'mod':
        if (isMacPlatform()) result.meta = true;
        else result.ctrl = true;
        break;
      default:
        throw new SyntaxError(`unknown modifier "${mod}" in hotkey "${combo}"`);
    }
  }
  return result;
}

const CODE_FALLBACK: Readonly<Record<string, readonly string[]>> = {
  '[': ['BracketLeft'],
  ']': ['BracketRight'],
  '-': ['Minus', 'NumpadSubtract'],
  '=': ['Equal'],
  '+': ['NumpadAdd'],
};

function codesForKey(key: string): readonly string[] {
  if (/^[a-z]$/.test(key)) return [`Key${key.toUpperCase()}`];
  if (/^[0-9]$/.test(key)) return [`Digit${key}`, `Numpad${key}`];
  return CODE_FALLBACK[key] ?? [];
}

/** True if `event` is the key combination `combo`. */
export function matchesCombo(combo: Combo, event: KeyboardEvent): boolean {
  if (event.ctrlKey !== combo.ctrl || event.altKey !== combo.alt || event.metaKey !== combo.meta) {
    return false;
  }
  const punctuation = combo.key.length === 1 && !/[a-z0-9 ]/.test(combo.key);
  if (!(punctuation && !combo.shift) && event.shiftKey !== combo.shift) return false;
  if (event.key.toLowerCase() === combo.key) return true;
  // Alt/Shift change `event.key` (⌥L → "¬", ⇧1 → "!"): fall back to the physical key.
  return (event.altKey || event.shiftKey) && codesForKey(combo.key).includes(event.code);
}

const NON_TYPING_INPUTS = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'file',
  'image',
]);

/** True when keystrokes on `target` are text entry (hotkeys must not steal them). */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) return !NON_TYPING_INPUTS.has(target.type);
  return false;
}

// --------------------------------------------------------------------------- registry

interface Registration {
  id: number;
  combo: string;
  parsed: Combo;
  handler: HotkeyHandler;
  scope: string;
  allowInInputs: boolean;
  preventDefault: boolean;
  repeat: boolean;
  description: string | undefined;
}

const registrations: Registration[] = [];
const scopeStack: { id: number; scope: string }[] = [];
let nextId = 1;
let listening = false;

/** The scope whose bindings currently fire. */
export function activeHotkeyScope(): string {
  return scopeStack.at(-1)?.scope ?? GLOBAL_SCOPE;
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.defaultPrevented || event.isComposing) return;
  const scope = activeHotkeyScope();
  const typing = isTypingTarget(event.target);
  for (let i = registrations.length - 1; i >= 0; i--) {
    const reg = registrations[i];
    if (reg.scope !== scope) continue;
    if (typing && !reg.allowInInputs) continue;
    if (event.repeat && !reg.repeat) continue;
    if (!matchesCombo(reg.parsed, event)) continue;
    if (reg.handler(event) === false) continue;
    if (reg.preventDefault) event.preventDefault();
    return;
  }
}

function updateListener(): void {
  const needed = registrations.length > 0;
  if (needed && !listening) window.addEventListener('keydown', onKeyDown);
  if (!needed && listening) window.removeEventListener('keydown', onKeyDown);
  listening = needed;
}

/** Imperative registration; returns the unregister function. */
export function registerHotkey(
  combo: string,
  handler: HotkeyHandler,
  options: HotkeyOptions = {},
): () => void {
  const reg: Registration = {
    id: nextId++,
    combo,
    parsed: parseCombo(combo),
    handler,
    scope: options.scope ?? GLOBAL_SCOPE,
    allowInInputs: options.allowInInputs ?? false,
    preventDefault: options.preventDefault ?? true,
    repeat: options.repeat ?? false,
    description: options.description,
  };
  registrations.push(reg);
  updateListener();
  return () => {
    const index = registrations.findIndex((r) => r.id === reg.id);
    if (index >= 0) registrations.splice(index, 1);
    updateListener();
  };
}

/** Pushes a scope on the stack; returns the function that removes it again. */
export function pushHotkeyScope(scope: string): () => void {
  const entry = { id: nextId++, scope };
  scopeStack.push(entry);
  return () => {
    const index = scopeStack.findIndex((e) => e.id === entry.id);
    if (index >= 0) scopeStack.splice(index, 1);
  };
}

/** Described bindings of the active scope (for a "keyboard shortcuts" help panel). */
export function listHotkeys(): { combo: string; description: string }[] {
  const scope = activeHotkeyScope();
  const seen = new Set<string>();
  const out: { combo: string; description: string }[] = [];
  for (let i = registrations.length - 1; i >= 0; i--) {
    const reg = registrations[i];
    if (reg.scope !== scope || !reg.description || seen.has(reg.combo)) continue;
    seen.add(reg.combo);
    out.push({ combo: reg.combo, description: reg.description });
  }
  return out.reverse();
}

// --------------------------------------------------------------------------- React hooks

export type HotkeyBinding = HotkeyHandler | ({ handler: HotkeyHandler } & Omit<HotkeyOptions, 'scope'>);

/**
 * Registers `bindings` (combo → handler) while mounted and `enabled`. Handlers may be
 * inline closures: the latest ones are always called, but bindings are only
 * re-registered when the set of combos or their options change.
 *
 * ```ts
 * useHotkeys({ v: () => setTool('select'), 'mod+z': undo, '[': { handler: shrink, repeat: true } });
 * ```
 */
export function useHotkeys(
  bindings: Readonly<Record<string, HotkeyBinding>>,
  options: HotkeyOptions & { enabled?: boolean } = {},
): void {
  const latest = useRef(bindings);
  useLayoutEffect(() => {
    latest.current = bindings;
  });
  const { enabled = true, ...defaults } = options;
  const signature = JSON.stringify([
    defaults,
    Object.entries(bindings).map(([combo, b]) =>
      typeof b === 'function' ? [combo] : [combo, { ...b, handler: undefined }],
    ),
  ]);
  useEffect(() => {
    if (!enabled) return;
    const [base, entries] = JSON.parse(signature) as [HotkeyOptions, [string, HotkeyOptions?][]];
    const unregister = entries.map(([combo, own]) =>
      registerHotkey(
        combo,
        (event) => {
          const binding = latest.current[combo];
          if (!binding) return false;
          return typeof binding === 'function' ? binding(event) : binding.handler(event);
        },
        { ...base, ...own },
      ),
    );
    return () => unregister.forEach((fn) => fn());
  }, [signature, enabled]);
}

/** Makes `scope` the active hotkey scope while mounted and `active`. */
export function useHotkeyScope(scope: string, active = true): void {
  useEffect(() => (active ? pushHotkeyScope(scope) : undefined), [scope, active]);
}

// --------------------------------------------------------------------------- display

const MAC_MODIFIERS = { ctrl: '⌃', alt: '⌥', shift: '⇧', meta: '⌘' } as const;
const PC_MODIFIERS = { ctrl: 'Ctrl', alt: 'Alt', shift: 'Shift', meta: 'Win' } as const;
const KEY_LABELS: Readonly<Record<string, string>> = {
  ' ': 'Space',
  escape: 'Esc',
  enter: 'Enter',
  backspace: 'Backspace',
  delete: 'Del',
  tab: 'Tab',
  arrowleft: '←',
  arrowright: '→',
  arrowup: '↑',
  arrowdown: '↓',
  pageup: 'PgUp',
  pagedown: 'PgDn',
};

/** Display parts of a combo, e.g. `["Ctrl", "Shift", "Z"]` or `["⌘", "⇧", "Z"]` on macOS. */
export function comboParts(combo: string): string[] {
  const parsed = parseCombo(combo);
  const labels = isMacPlatform() ? MAC_MODIFIERS : PC_MODIFIERS;
  const parts: string[] = [];
  if (parsed.ctrl) parts.push(labels.ctrl);
  if (parsed.alt) parts.push(labels.alt);
  if (parsed.shift) parts.push(labels.shift);
  if (parsed.meta) parts.push(labels.meta);
  parts.push(KEY_LABELS[parsed.key] ?? (parsed.key.length === 1 ? parsed.key.toUpperCase() : parsed.key));
  return parts;
}

/** One-string display of a combo (`"Ctrl+Shift+Z"`, `"⌘⇧Z"`), e.g. for `title` attributes. */
export function formatCombo(combo: string): string {
  return comboParts(combo).join(isMacPlatform() ? '' : '+');
}
